/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IStackItemProps, IStackItemSlots, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logURlLinkClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { noop } from 'src/core/utils/base';

export type linkOption = {
    key: string;
    href: string;
    text: string;
};

type InfoCardWithExternalLinkProps = {
    linkHref?: string;
    linkText?: string;
    cardTitle: string;
    pageName: string;
    linkKey?: string;
    projectId?: string;
    cardDescription: string;
    featureName: TelemetryFeatureTitle;
    isLinkDisabled?: boolean;
    linkOptions?: linkOption[];
    optionsDropDownTitle?: string;
};

const messages = defineMessages({
    defaultOptionsDropDownTitle: {
        id: 'core.infoCardWithExternalLink.defaultOptionsDropDownTitle',
        defaultMessage: 'Links'
    },
    splitButtonAriaLabel: {
        id: 'core.infoCardWithExternalLink.splitButtonAriaLabel',
        defaultMessage: 'Show options'
    }
});

const sectionTheme = getScopedTheme('InfoCardWithExternalLink');

const descriptionTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { color: sectionTheme.descriptionTextColor }
});

const containerStyles = classNamesFunction<IStackItemProps, IComponentStyles<IStackItemSlots>>()({
    root: {
        maxWidth: 260,
        borderRadius: 8,
        cursor: 'pointer',
        boxShadow: sectionTheme.boxShadow,
        '&:hover': { boxShadow: sectionTheme.hoverBoxShadow }
    }
});

const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '14px' } });

const samplesStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    menuIcon: { color: sectionTheme.black },
    root: { height: 'auto', cursor: 'pointer' }
});

const getLinkView = (
    link: string,
    text: string,
    key: string,
    featureName: TelemetryFeatureTitle,
    pageName: string,
    projectId: string,
    disabled?: boolean
) => (
    <ExternalLink href={link} disabled={disabled} onClick={() => logURlLinkClick(featureName, pageName, key, projectId)}>
        <Stack horizontal verticalAlign="baseline">
            <LuisText>
                {text}
                &nbsp;
            </LuisText>
            <Icon iconName="MiniExpand" styles={iconStyles} />
        </Stack>
    </ExternalLink>
);

const theme = getScopedTheme('ExternalLink');

const getSubLinkView = (text: string, disabled: boolean) => (
    <Stack
        horizontal
        verticalAlign="baseline"
        styles={{
            root: {
                textDecoration: 'underline',
                color: !disabled && `${theme.lightModeColor} !important`
            }
        }}>
        <LuisText>
            {text}
            &nbsp;
        </LuisText>
        <Icon iconName="MiniExpand" styles={iconStyles} />
    </Stack>
);

const getSamplesLinkOptions = (
    linkOptions: linkOption[],
    featureName: TelemetryFeatureTitle,
    pageName: string,
    projectId: string,
    isLinkDisabled: boolean
): IContextualMenuItem[] =>
    linkOptions.map(link => ({
        key: link.key,
        href: link.href,
        target: '_blank',
        ariaLabel: link.text,
        disabled: isLinkDisabled,
        onClick: () => logURlLinkClick(featureName, pageName, link.key, projectId),
        onRenderContent: () => getSubLinkView(link.text, isLinkDisabled)
    }));

/**
 * Represents a card with some info and external link(s).
 *
 * @param props The properties passed to the card.
 */
export const InfoCardWithExternalLink = (props: InfoCardWithExternalLinkProps) => {
    const {
        linkHref,
        linkKey,
        linkText,
        cardTitle,
        featureName,
        pageName,
        projectId,
        cardDescription,
        isLinkDisabled = false,
        linkOptions,
        optionsDropDownTitle
    } = props;

    const { localize } = useLocalization();

    return (
        <Stack styles={containerStyles} tokens={{ padding: '11px 9px', childrenGap: 14 }}>
            <LuisText fontWeight={500}>{cardTitle}</LuisText>

            <Stack verticalFill verticalAlign="space-between" tokens={{ childrenGap: 20 }}>
                <LuisText styles={descriptionTextStyles}>{cardDescription}</LuisText>

                {linkOptions ? (
                    <ActionButton
                        onClick={noop}
                        data-is-focusable
                        styles={samplesStyles}
                        menuIconProps={{ iconName: 'ChevronDown', title: 'Dropdown icon' }}
                        menuProps={{ items: getSamplesLinkOptions(linkOptions, featureName, pageName, projectId, isLinkDisabled) }}>
                        {optionsDropDownTitle ?? localize(messages.defaultOptionsDropDownTitle)}
                    </ActionButton>
                ) : (
                    getLinkView(linkHref, linkText, linkKey, featureName, pageName, projectId, isLinkDisabled)
                )}
            </Stack>
        </Stack>
    );
};
